import {
    mdiCog,
    mdiDoorClosed,
    mdiHomeGroup,
    mdiOfficeBuilding,
    mdiShapeOutline,
    mdiTree,
} from '@mdi/js'

import {
    ICON_TILE_SIZES,
    IconTile,
    type IconTileGradient,
    type IconTileSize,
    type IconTileTone,
} from '../ui/icon-tile'
import { iconPathFor } from '../../lib/asset-type-icons'
import { cn } from '../../lib/utils'
import { lotBaseTypeCode } from '../../lib/asset-type-tree'

/**
 * `AssetTypeTile` — the ONE way the app draws what an asset is
 * (spec 2026-09-17-04). It replaced the Unicode emoji everywhere: the tree,
 * the detail header, the favourites, the pickers, the relation rows and the
 * type admin.
 *
 * Two things it carries that an emoji could not: a machine-readable glyph
 * (`asset_types.icon`, an MDI name) and a FAMILY COLOUR, so a column of forty
 * rows reads as sites / buildings / units / equipment at a glance instead of
 * as forty unrelated pictograms.
 */

/** The shape every call site has, whatever query it came from. */
export interface AssetTypeLike {
    code?: string | null
    kind?: string | null
    icon?: string | null
    emoji?: string | null
    properties?: Record<string, unknown> | null
}

/** The per-asset override: `assets.emoji`, set by a user on one asset. */
export interface AssetLike {
    emoji?: string | null
}

/** Codes classified `location` by the backend (internal/handlers/assets/kinds.go). */
const LOCATION_CODES = new Set([
    'building',
    'building_group',
    'common_area',
    'exterior_space',
    'floor',
    'house',
    'outdoor',
    'parcel_land',
    'room',
    'space',
    'unit',
])

/** Locations that are ground/outside rather than built volume. */
const SPACE_CODES = new Set([
    'outdoor',
    'exterior_space',
    'space_parking',
    'parcel_land',
])

/**
 * The kind the backend would report, when the payload at hand does not carry
 * one. Mirrors `KindOf` minus the ancestry it cannot see: a `room_*` or
 * `space_*` code is a location, a `trade_*` code is a trade, and a code that
 * starts with a known trade's leaf prefix is equipment.
 */
function inferKind(code: string): string {
    if (code.startsWith('trade_')) return 'trade'
    if (LOCATION_CODES.has(lotBaseTypeCode(code))) return 'location'
    if (code.startsWith('room_') || code.startsWith('space_')) return 'location'
    if (code === 'equipment') return 'equipment'
    return 'other'
}

/**
 * The family colour of an asset type — derived, never stored, from fields the
 * type already has. First match wins; the table is the spec's.
 */
export function assetTypeGradient(type?: AssetTypeLike | null): IconTileGradient {
    if (!type) return 'neutral'
    const code = type.code ?? ''
    const kind = type.kind || inferKind(code)

    if (code === 'building_group') return 'asset-site'
    if (kind === 'location') {
        if (type.properties?.private === true) return 'asset-unit'
        if (code === 'room' || code.startsWith('room_')) return 'asset-unit'
        if (SPACE_CODES.has(code)) return 'asset-space'
        return 'asset-building'
    }
    if (code.startsWith('trade_site') || code.startsWith('site_')) {
        return 'asset-space'
    }
    if (kind === 'trade' || kind === 'equipment' || kind === 'structural') {
        return 'asset-equipment'
    }
    return 'neutral'
}

/** The glyph a family falls back to when the type names no icon of its own. */
export const ASSET_FAMILY_FALLBACK_ICON: Record<IconTileGradient, string> = {
    'asset-site': mdiHomeGroup,
    'asset-building': mdiOfficeBuilding,
    'asset-unit': mdiDoorClosed,
    'asset-space': mdiTree,
    'asset-equipment': mdiCog,
    neutral: mdiShapeOutline,
    brand: mdiShapeOutline,
    economic: mdiShapeOutline,
    incidents: mdiShapeOutline,
    compliance: mdiShapeOutline,
}

export interface AssetTypeTileProps {
    type?: AssetTypeLike | null
    /**
     * The asset being drawn, when there is one. Its own `emoji` — a
     * customisation a user made on THIS asset — wins over the type's glyph and
     * is drawn on a neutral, soft tile.
     */
    asset?: AssetLike | null
    size?: IconTileSize
    tone?: IconTileTone
    className?: string
    /** Passed through to IconTile: without it the tile is `aria-hidden`. */
    label?: string
    title?: string
}

/**
 * Draw an asset's type. The glyph ladder is: the asset's own emoji → the
 * type's `icon` → the family's default glyph.
 */
function AssetTypeTile({
    type,
    asset,
    size = 'sm',
    tone = 'solid',
    className,
    label,
    title,
}: AssetTypeTileProps) {
    const gradient = assetTypeGradient(type)
    const customEmoji = asset?.emoji?.trim()

    if (customEmoji) {
        // Drawn by hand rather than through IconTile's `initial`, which slices
        // to one UTF-16 unit and would cut a surrogate pair in half — every
        // emoji above U+FFFF, which is most of them.
        const { box, radius } = ICON_TILE_SIZES[size]
        return (
            <span
                data-testid="asset-type-tile"
                data-icon="emoji"
                data-gradient="neutral"
                title={title ?? type?.code ?? undefined}
                aria-hidden={label ? undefined : true}
                className={cn(
                    'inline-flex shrink-0 items-center justify-center leading-none',
                    className
                )}
                style={{
                    width: box,
                    height: box,
                    borderRadius: radius,
                    fontSize: Math.round(box * 0.6),
                    backgroundColor:
                        'color-mix(in oklch, var(--gradient-neutral-from) 10%, transparent)',
                }}
            >
                {customEmoji}
                {label ? <span className="sr-only">{label}</span> : null}
            </span>
        )
    }

    const name = type?.icon ?? undefined
    const path = iconPathFor(name) ?? ASSET_FAMILY_FALLBACK_ICON[gradient]

    return (
        <span
            data-testid="asset-type-tile"
            data-icon={iconPathFor(name) ? name : `family:${gradient}`}
            data-gradient={gradient}
            title={title ?? type?.code ?? undefined}
            className="inline-flex shrink-0"
        >
            <IconTile
                gradient={gradient}
                size={size}
                tone={tone}
                path={path}
                className={className}
                label={label}
            />
        </span>
    )
}

export { AssetTypeTile }
